import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import Select from 'components/CustomSelect/Select';
import RegularCheckbox from 'components/Checkbox/RegularCheckbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { sortByKey } from 'helpers/helpers';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';
import { get } from 'helpers/apiHelpers';
import constants from 'helpers/constants';
import { Tooltip } from '@material-ui/core';
import { Info } from '@material-ui/icons';
import SecuredView from 'components/Security/SecuredView';
import { ROLE_SHOW_ADDON } from 'helpers/roles';
import makeStyles from '@material-ui/styles/makeStyles';
import { toast } from 'react-toastify';
import moment from 'moment';

import AdditionalMealTypeSection from '../AdditionalMealTypeSection/AdditionalMealTypeSection';
import DeliveryAddressSection from '../DeliveryAddressSection/DeliveryAddressSection';
import DietSummarySection from '../DietSummarySection/DietSummarySection';
import DietNameSection from '../DietNameSection/DietNameSection';
import CalendarSection from '../CalendarSection/CalendarSection';
import AddonsSection from '../AddonsSection/AddonsSection';
import BoxSection from '../BoxSection';

const useStyles = makeStyles({
  dietConfigurationWrapper: {
    display: 'flex',
    width: '100%',
    margin: '0 -5px',
    flexFlow: 'column',
    '@media (min-width: 769px)': {
      flexFlow: 'row',
    },
  },
  dietConfigurationSectionsWrapper: {
    width: '100%',
    margin: '5px',
  },
  parentType: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '15px',
    justifyContent: 'space-between',
  },
  dietDetails: {
    width: '60%',
    marginLeft: '15px',
  },
  dietDetailsTest: {
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    marginLeft: '-10px',
    color: 'grey',
    fontSize: '16px',
  },
  boxSection: {
    margin: '15px 0 0',
  },
  flex: { display: 'flex' },
  divider: {
    paddingBottom: '15px',
    borderBottom: `1px solid ${grayColor[4]}`,
  },
});

const DietConfigurationSection = ({
  diet = {},
  classes: propClasses,
  dietCost = {},
  pickUpPoints = [],
  deliveryTypes = [],
  selectedCustomer,
  calendarSettings,
  availableAddons = [],
  customerAddresses = [],
  useTestMode,
  isAllowedPremium,
  isAllowedTestDays,
  isMoreThanOneDiet,
  isAllowedEcoContainer,
  dietIndex,
  brandInfo,
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- FIXME
  updateDiet = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- FIXME
  setUseTestMode = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- FIXME
  updateCustomerAddresses = () => {},
}) => {
  const { t } = useTranslation();

  const {
    selectedDiet,
    selectedVariant,
    selectedSuperVariant,
    useEcoContainers = false,
    optionChangeMenu = false,
    selectedCalorific,
  } = diet ?? {};

  const classes = useStyles();

  const [availableVariants, setAvailableVariants] = useState([]);
  const [availableCalorifics, setAvailableCalorifics] = useState([]);
  const [dietsCost, setDietsCost] = useState([]);
  const [additionalPrices, setAdditionalPrices] = useState([]);
  const [availableMealTypes, setAvailableMealTypes] = useState([]);
  const [superVariantMode, setSuperVariantMode] = useState(false);

  const [allowedMealTypes, setAllowedMealTypes] = useState([]);

  const [selectedOptions, setSelectedOptions] = useState([]);

  const [selectedDietAdditionalCalendar, setSelectedDietAdditionalCalendar] =
    useState([]);
  const [editAdditionalMealTypes, setEditAdditionalMealTypes] = useState('');
  const [deliveryType, setDeliveryType] = useState(deliveryTypes?.[0] ?? null);

  useEffect(() => {
    if (deliveryTypes && deliveryTypes.length > 0) {
      setDeliveryType(deliveryTypes[0]);
    }
  }, [deliveryTypes]);

  const { diets, variants, mealTypes, currency } = useSelector(state => {
    // eslint-disable-next-line no-shadow -- FIXME
    const variants = sortByKey(state.Variants.variants, 'position').sort(
      (_, b) => (b.active ? 1 : -1)
    );
    // eslint-disable-next-line no-shadow -- FIXME
    const diets = sortByKey(state.Diets.diets, 'position')
      .sort((_, b) => (b.active ? 1 : -1))
      // eslint-disable-next-line no-shadow -- FIXME
      .map(diet => ({
        label: `${diet?.name} ${
          diet?.active ? '' : `(${t('$*common.inactive.diet', '$$Nieaktywna')})`
        }`,
        value: diet?.['@id'],
      }));

    // eslint-disable-next-line no-shadow -- FIXME
    const { mealTypes } = state.MealTypes;

    return {
      diets,
      variants,
      mealTypes,
      currency: state?.Brands?.brand?.multinational?.currency,
    };
  });

  useEffect(() => {
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    get('/diet-costs', { pagination: false }).then(response => {
      setDietsCost(response['hydra:member']);
    });
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    get('/additional-prices').then(response => {
      // eslint-disable-next-line no-shadow -- FIXME
      const additionalPrices = response['hydra:member'];
      // eslint-disable-next-line promise/prefer-await-to-then, no-shadow -- FIXME
      get('/additional-price-types').then(response => {
        const additionalPricesTypes = response['hydra:member'];
        const additionalPricesWithAmount = additionalPrices?.map(item => {
          const types = additionalPricesTypes?.filter(
            type => type['@id'] === item?.additionalPriceType
          );
          return {
            systemValue: types[0]?.systemValue,
            price: item?.discount,
          };
        });
        setAdditionalPrices(additionalPricesWithAmount);
      });
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(selectedDiet)) {
      const filteredVariants = variants
        .filter(variant => (variant?.diets ?? []).includes(selectedDiet?.value))
        .sort((_, b) => (b.active ? 1 : -1))
        .map(variant => ({
          value: variant?.['@id'],
          label: `${variant?.name} ${
            variant?.active
              ? ''
              : `(${t('$*common.inactive.variant', '$$Nieaktywny')})`
          }`,
          mealTypes: variant.mealTypes ?? [],
          calories: variant.calories ?? [],
          superVariantBeta: variant?.superVariantBeta,
          minPriceVariant: variant?.minPrice ?? null,
          minMealTypesVariant: variant.minMealTypes ?? null,
        }));
      setSuperVariantMode(false);
      if (
        filteredVariants.length === 1 &&
        filteredVariants[0].superVariantBeta
      ) {
        updateDiet({
          ...diet,
          selectedVariant: filteredVariants[0],
          selectedCalorific: null,
        });
        setSuperVariantMode(true);
      }
      setAvailableVariants(filteredVariants);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [selectedDiet]);

  const pricesChangeMenu = additionalPrices?.filter(
    el => el?.systemValue === 'CHANGE_MENU'
  );

  const pricesEcoContainer = additionalPrices?.filter(
    el => el?.systemValue === 'ECO_CONTAINER'
  );
  // Update available calorifics and set avalaibleMealTypes
  useEffect(() => {
    if (isEmpty(selectedVariant) || isEmpty(dietsCost)) {
      return;
    }

    if (selectedVariant.superVariantBeta) {
      const foundVariant = variants.find(
        variant => variant['@id'] === selectedVariant.value
      );
      const mealTypeFullData = mealTypes.filter(mealType =>
        foundVariant?.mealTypes.includes(mealType['@id'])
      );

      const mealTypesConverted = mealTypeFullData.map(el => ({
        value: el['@id'],
        label: el.name,
      }));
      setAvailableMealTypes(mealTypesConverted);
    }

    const currentVariantCalories = variants.find(
      variant => variant?.['@id'] === selectedVariant?.value
    )?.calories ?? [{}];

    const formattedCalorifics = currentVariantCalories
      .sort((_, b) => (b.active ? 1 : -1))
      .map(calorific => ({
        value: calorific?.['@id'],
        label: `${calorific?.name} ${
          calorific?.active ? '' : `(${t('$*common.inactive.calorific')})`
        }`,
      }));

    const calorificsIri = formattedCalorifics?.map(
      calorific => calorific?.value
    );
    const dietIri = selectedDiet?.value;

    const filteredDiet = dietsCost?.filter(
      ({ diet: iri, calorific }) =>
        iri === dietIri && calorificsIri.includes(calorific)
    );
    const updatedCalorifics = formattedCalorifics?.map(item => {
      const filteredCosts = filteredDiet?.filter(
        cost => cost?.calorific === item?.value
      );
      const price =
        filteredCosts[0]?.price === 999 ||
        filteredCosts[0]?.price === null ||
        filteredCosts[0]?.price === undefined
          ? t('orders.noPrice')
          : `(${
              useTestMode
                ? filteredCosts[0]?.priceForTest
                : filteredCosts[0]?.price
            } ${`${currency} ${t(
              'orders.calorificDescriptionCurrencyPerDay'
            )}`})`;

      return {
        ...item,
        label: `${item?.label} ${price}`,
      };
    });
    setAvailableCalorifics(updatedCalorifics);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [selectedVariant, dietsCost, useTestMode]);

  useEffect(() => {
    if (mealTypes && selectedVariant && selectedCalorific) {
      const allowedAdditionalMealTypes = mealTypes
        .filter(mealType => selectedVariant.mealTypes.includes(mealType['@id']))
        .map(mealType => {
          // eslint-disable-next-line array-callback-return, consistent-return -- FIXME
          const activeCalorific = selectedVariant.calories.filter(calories => {
            if (calories['@id'] === selectedCalorific.value) {
              return calories;
            }
          });

          // eslint-disable-next-line array-callback-return, consistent-return -- FIXME
          const activeCalorificSize = activeCalorific[0].sizes.filter(sizes => {
            if (sizes.mealType === mealType['@id']) {
              return sizes;
            }
          });

          // eslint-disable-next-line array-callback-return, consistent-return -- FIXME
          const activeMealTypeSize = mealType.sizes.filter(sizes => {
            if (sizes['@id'] === activeCalorificSize[0].size) {
              return sizes;
            }
          });

          return {
            value: mealType['@id'],
            label: mealType.name,
            price: activeMealTypeSize[0]?.defaultCostForAdditionalMealType,
            date: [],
          };
        });
      setAllowedMealTypes(allowedAdditionalMealTypes);
    }
    if (selectedCalorific === null) {
      setAllowedMealTypes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [selectedCalorific]);

  useEffect(() => {
    updateDiet({
      ...diet,
      selectedSuperVariant: availableMealTypes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [availableMealTypes]);

  const handleUpdateDiet = (key, value) => {
    updateDiet({ ...diet, [key]: value });
  };

  const handleDietChange = option => {
    updateDiet({
      ...diet,
      selectedDiet: option,
      selectedVariant: null,
      selectedSuperVariant: null,
      selectedCalorific: null,
      selectedAdditionalMealTypes: [],
    });
    setAllowedMealTypes([]);
    setSelectedOptions([]);
  };

  const handleVariantChange = option => {
    setAllowedMealTypes([]);
    setSelectedOptions([]);
    updateDiet({
      ...diet,
      selectedVariant: option,
      selectedCalorific: null,
      selectedAdditionalMealTypes: [],
    });
  };

  const handleCalorificChange = option => {
    setSelectedOptions([]);
    updateDiet({
      ...diet,
      selectedCalorific: option,
      selectedAdditionalMealTypes: [],
    });
  };

  const handleSuperVariantChange = option => {
    updateDiet(
      {
        ...diet,
        selectedSuperVariant: option,
      },
      dietIndex || 0
    );
  };

  const [urlDietParams, setUrlDietParams] = useState(null);
  const [autoFillStep, setAutoFillStep] = useState(0);

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const paramDate = params.get('date');
    const paramDiet = params.get('diet');
    const paramVariant = params.get('variant');
    const paramCalorific = params.get('calorific');
    const paramOptionChangeMenu = params.get('option-change-menu') === '1';
    const paramEcoContainers = params.get('eco-containers') === '1';
    const paramAddress = params.get('address');
    const paramPickUpPoint = params.get('pick-up-point');
    const paramMealTypes = params.getAll('meal-types');

    if (!paramDate) {
      return;
    }

    updateDiet(
      {
        ...diet,
        selectedDays: [moment(paramDate)],
        dietLength: 1,
        useEcoContainers: paramEcoContainers,
        optionChangeMenu: paramOptionChangeMenu,
        address:
          customerAddresses.find(addr => addr['@id'] === paramAddress) || null,
        pickUpPoint:
          pickUpPoints.find(pup => pup.value === paramPickUpPoint) || null,
      },
      0
    );

    setDeliveryType(paramPickUpPoint ? deliveryTypes[1] : deliveryTypes[0]);
    setUrlDietParams({
      paramDiet,
      paramVariant,
      paramCalorific,
      paramMealTypes,
    });
    setAutoFillStep(1);
  }, []);

  useEffect(() => {
    if (autoFillStep === 1 && urlDietParams && diets.length > 0) {
      const matchedDiet = diets.find(
        dietOption => dietOption.value === urlDietParams.paramDiet
      );
      if (matchedDiet) {
        handleDietChange(matchedDiet);
        setAutoFillStep(2);
      }
    }
  }, [autoFillStep, urlDietParams, diets]);

  useEffect(() => {
    if (autoFillStep === 2 && urlDietParams && availableVariants.length > 0) {
      const matchedVariant = availableVariants.find(
        variantOption => variantOption.value === urlDietParams.paramVariant
      );
      if (matchedVariant) {
        handleVariantChange(matchedVariant);
        setAutoFillStep(3);
      }
    }
  }, [autoFillStep, urlDietParams, availableVariants]);

  useEffect(() => {
    if (autoFillStep === 2 && urlDietParams && availableMealTypes.length > 0) {
      const matchedMealTypes = availableMealTypes.filter(mealTypeOption =>
        urlDietParams.paramMealTypes.includes(mealTypeOption.value)
      );
      if (matchedMealTypes.length > 0) {
        handleSuperVariantChange(matchedMealTypes);
        setAutoFillStep(3);
      }
    }
  }, [autoFillStep, urlDietParams, availableMealTypes]);

  useEffect(() => {
    if (autoFillStep === 3 && urlDietParams && availableCalorifics.length > 0) {
      const matchedCalorific = availableCalorifics.find(
        calorificOption =>
          calorificOption.value === urlDietParams.paramCalorific
      );
      if (matchedCalorific) {
        handleCalorificChange(matchedCalorific);
        setAutoFillStep(0);
      }
    }
  }, [autoFillStep, urlDietParams, availableCalorifics]);

  const handleTestModeCheck = () => {
    if (isAllowedTestDays) {
      setUseTestMode(!useTestMode);
    }
  };

  return (
    <div className={classes.dietConfigurationWrapper}>
      <div className={classes.dietConfigurationSectionsWrapper}>
        <BoxSection title={t('orders.dietDetails', '$$Szczegóły diety')}>
          <div className={classes.parentType}>
            <p className={propClasses.labelBlack}>
              {t('orders.dietType', '$$Rodzaj diety')}*
            </p>
            <div className={classes.dietDetails} data-cy="diet-details_diets">
              <Select
                label={t('orders.select', '$$Wybierz')}
                value={selectedDiet}
                options={diets}
                handleChange={handleDietChange}
              />
            </div>
          </div>
          {!superVariantMode && (
            <div className={classes.parentType}>
              <p className={propClasses.labelBlack}>
                {t('orders.variant', '$$Wariant')}*
              </p>
              <div
                data-cy="diet-details_variants"
                className={classes.dietDetails}
                onClick={() =>
                  isEmpty(selectedDiet)
                    ? toast.info(t('orders.firstSelectDate'))
                    : null
                }
              >
                <Select
                  label={t('orders.select', '$$Wybierz')}
                  value={selectedVariant}
                  options={availableVariants}
                  isDisabled={isEmpty(selectedDiet)}
                  handleChange={handleVariantChange}
                />
              </div>
            </div>
          )}
          {selectedVariant?.superVariantBeta && (
            <div className={classes.parentType}>
              <p className={propClasses.labelBlack}>
                {superVariantMode
                  ? t('orders.variant', '$$Wariant')
                  : t('orders.superVariantBeta', '$$Super wariant')}
                *
              </p>
              <div
                // eslint-disable-next-line react/no-unknown-property -- FIXME
                ata-cy="diet-details_calorific"
                className={classes.dietDetails}
                onClick={() =>
                  isEmpty(selectedDiet)
                    ? toast.info(t('orders.firstSelectDate'))
                    : null
                }
              >
                <Select
                  isClearable={false}
                  label={t('orders.select', '$$Wybierz')}
                  value={selectedSuperVariant}
                  options={availableMealTypes}
                  isDisabled={isEmpty(selectedDiet)}
                  multiple={true}
                  handleChange={handleSuperVariantChange}
                />
              </div>
            </div>
          )}
          <div className={classes.parentType}>
            <p className={propClasses.labelBlack}>
              {t('orders.calorific', '$$Kaloryczność')}*
            </p>
            <div
              className={classes.dietDetails}
              onClick={() =>
                isEmpty(selectedVariant)
                  ? toast.info(t('orders.firstSelectVariant'))
                  : null
              }
            >
              <Select
                label={t('orders.select', '$$Wybierz')}
                value={selectedCalorific}
                options={availableCalorifics}
                isDisabled={isEmpty(selectedVariant)}
                handleChange={handleCalorificChange}
              />
            </div>
          </div>
          <div className={classes.divider}></div>
          <DietNameSection
            diet={diet}
            classes={propClasses}
            updateDiet={updateDiet}
          />

          {isAllowedPremium === constants.PREMIUM ? (
            <div data-cy="diet-details_premium">
              <FormControlLabel
                control={
                  <RegularCheckbox
                    name="active"
                    checked={optionChangeMenu}
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- FIXME
                    onClick={e => {
                      handleUpdateDiet('optionChangeMenu', !optionChangeMenu);
                    }}
                  />
                }
                label={
                  t('orders.possibilitySelectMeals') +
                  (pricesChangeMenu[0]?.price === null ||
                  pricesChangeMenu[0]?.price === undefined
                    ? ''
                    : ` (${pricesChangeMenu[0]?.price}${` ${currency} ${t(
                        'orders.calorificDescriptionCurrencyPerDay'
                      )}`})`)
                }
              />
            </div>
          ) : null}

          {isAllowedEcoContainer ? (
            <div data-cy="diet-details_eco-container">
              <FormControlLabel
                control={
                  <RegularCheckbox
                    name="active"
                    checked={useEcoContainers}
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- FIXME
                    onClick={e => {
                      handleUpdateDiet('useEcoContainers', !useEcoContainers);
                    }}
                  />
                }
                label={
                  t('orders.newOrder.DietConfigurationSection.ecoBoxes') +
                  (pricesEcoContainer[0]?.price === null ||
                  pricesEcoContainer[0]?.price === undefined
                    ? ''
                    : ` (${pricesEcoContainer[0]?.price}${` ${currency} ${t(
                        'orders.ecoContainerDescriptionCurrencyPerDay'
                      )}`})`)
                }
              />
            </div>
          ) : null}

          {!isMoreThanOneDiet && (
            <div
              data-cy="diet-details_test-diet"
              className={classes.dietDetailsTest}
            >
              <FormControlLabel
                disabled={!isAllowedTestDays}
                control={
                  <RegularCheckbox
                    name="active"
                    checked={useTestMode}
                    onClick={handleTestModeCheck}
                  />
                }
                label={t(
                  'orders.newOrder.DietConfigurationSection.testDays',
                  'Zamówienie testowe'
                )}
              />
              {!isAllowedTestDays && (
                <Tooltip
                  title={t(
                    'orders.newOrder.DietConfigurationSection.testDays.tooltip'
                  )}
                  placement="right"
                >
                  <Info className={classes.infoIcon} />
                </Tooltip>
              )}
            </div>
          )}
        </BoxSection>
        <BoxSection
          title={t('orders.deliveryAddress', '$$Adres dostawy')}
          style={{ margin: '15px 0 0' }}
        >
          <DeliveryAddressSection
            diet={diet}
            classes={propClasses}
            updateDiet={updateDiet}
            pickUpPoints={pickUpPoints}
            deliveryTypes={deliveryTypes}
            deliveryType={deliveryType}
            setDeliveryType={setDeliveryType}
            selectedCustomer={selectedCustomer}
            customerAddresses={customerAddresses}
            updateCustomerAddresses={updateCustomerAddresses}
          />
        </BoxSection>
      </div>
      <div className={classes.dietConfigurationSectionsWrapper}>
        <BoxSection title={t('orders.duration', '$$Czas trwania')}>
          <CalendarSection
            diet={diet}
            brandInfo={brandInfo}
            classes={propClasses}
            updateDiet={updateDiet}
            calendarSettings={calendarSettings}
            setSelectedOptions={setSelectedOptions}
          />
        </BoxSection>

        <div className={classes.flex}>
          <BoxSection
            title={t('orders.additionalMealTypes', '$$Dodatkowe porcje')}
            style={{ margin: '15px 5px 0  0' }}
          >
            <AdditionalMealTypeSection
              calendarSettings={calendarSettings}
              diet={diet}
              classes={propClasses}
              updateDiet={updateDiet}
              allowedMealTypes={allowedMealTypes}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              selectedDietAdditionalCalendar={selectedDietAdditionalCalendar}
              setSelectedDietAdditionalCalendar={
                setSelectedDietAdditionalCalendar
              }
              editAdditionalMealTypes={editAdditionalMealTypes}
              setEditAdditionalMealTypes={setEditAdditionalMealTypes}
            />
          </BoxSection>

          <SecuredView role={ROLE_SHOW_ADDON} returnNull={true}>
            <BoxSection
              title={t('orders.addons', '$$Dodatki')}
              style={{ margin: '15px 0 0 5px' }}
            >
              <AddonsSection
                diet={diet}
                classes={propClasses}
                updateDiet={updateDiet}
                availableAddons={availableAddons}
              />
            </BoxSection>
          </SecuredView>
        </div>
      </div>

      <div className={classes.dietConfigurationSectionsWrapper}>
        <DietSummarySection
          diet={diet}
          dietCost={dietCost}
          dietIndex={dietIndex}
        />
      </div>
    </div>
  );
};

export default DietConfigurationSection;
